<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const migrationSources = ref([]);
const selectedSourceInboxId = ref('');
const isLoading = ref(false);
const isSubmitting = ref(false);

const fetchMigrationSources = async () => {
  try {
    isLoading.value = true;
    const response = await whatsappChannel.getMigrationSources();
    migrationSources.value = response.data.payload || [];
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.MIGRATION.FETCH_ERROR') ||
        'Error al obtener los inboxes de migración'
    );
  } finally {
    isLoading.value = false;
  }
};

const handleMigrate = async () => {
  if (!selectedSourceInboxId.value) {
    useAlert(
      t('INBOX_MGMT.MIGRATION.SELECT_SOURCE_ERROR') ||
        'Por favor selecciona un inbox origen'
    );
    return;
  }

  if (selectedSourceInboxId.value === props.inbox.id.toString()) {
    useAlert(
      t('INBOX_MGMT.MIGRATION.SAME_INBOX_ERROR') ||
        'El inbox origen no puede ser el mismo que el destino'
    );
    return;
  }

  try {
    isSubmitting.value = true;
    await whatsappChannel.migrateInbox({
      sourceInboxId: selectedSourceInboxId.value,
      targetInboxId: props.inbox.id,
    });
    useAlert(
      t('INBOX_MGMT.MIGRATION.SUCCESS') ||
        'La migración se está ejecutando en segundo plano'
    );
    selectedSourceInboxId.value = '';
    
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.MIGRATION.ERROR') ||
        'Error al realizar la migración'
    );
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  fetchMigrationSources();
});
</script>

<template>
  <div class="gap-4 pt-8 mx-8">
    <div class="px-5 py-5 space-y-6 rounded-xl border shadow-sm border-n-weak bg-n-solid-2">
      <div class="flex flex-col gap-5 justify-between items-start w-full md:flex-row">
        <div>
          <span class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.TABS.MIGRATION') }}
          </span>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.MIGRATION.DESCRIPTION') || 'Migra conversaciones, mensajes y contactos desde un inbox origen a este inbox destino.' }}
          </p>
        </div>
      </div>

      <div v-if="isLoading" class="flex justify-center py-8">
        <p class="text-sm text-n-slate-11">{{ $t('INBOX_MGMT.MIGRATION.LOADING') || 'Cargando...' }}</p>
      </div>

      <div v-else class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INBOX_MGMT.MIGRATION.SOURCE_INBOX_LABEL') || 'Inbox Origen' }}
          </label>
          <select
            v-model="selectedSourceInboxId"
            class="block w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :disabled="isSubmitting"
          >
            <option value="">{{ $t('INBOX_MGMT.MIGRATION.SELECT_PLACEHOLDER') || 'Selecciona un inbox origen' }}</option>
            <option
              v-for="source in migrationSources"
              :key="source.id"
              :value="source.id"
            >
              {{ source.name }}
            </option>
          </select>
          <p v-if="migrationSources.length === 0" class="mt-2 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.MIGRATION.NO_SOURCES') || 'No hay inboxes disponibles para migración' }}
          </p>
        </div>

        <div class="flex justify-end pt-4">
          <button
            type="button"
            class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!selectedSourceInboxId || isSubmitting || migrationSources.length === 0"
            @click="handleMigrate"
          >
            <span v-if="isSubmitting">
              {{ $t('INBOX_MGMT.MIGRATION.MIGRATING') || 'Migrando...' }}
            </span>
            <span v-else>
              {{ $t('INBOX_MGMT.MIGRATION.MIGRATE_BUTTON') || 'Migrar' }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

